import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  Extension,
  ExtensionCreateInput,
  ExtensionType,
  ExtensionUpdateInput,
} from "@/types/entities";

const EXTENSION_TYPES: ExtensionType[] = ["public", "apikey", "cms"];

type FormProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: ExtensionCreateInput | ExtensionUpdateInput) => Promise<void>;
  onCancel?: () => void;
  extension?: Extension;
};

const HTTP_METHODS = ["GET", "POST", "PUT", "DELETE", "PATCH", "HEAD", "OPTIONS"];

export function Form({ open, onOpenChange, onSubmit, onCancel, extension }: FormProps) {
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [type, setType] = useState<ExtensionType>("cms");
  const [endpointPath, setEndpointPath] = useState("");
  const [endpointMethod, setEndpointMethod] = useState("GET");
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (extension) {
      setName(extension.name);
      setDisplayName(extension.display_name ?? "");
      setType(extension.type);
      setEndpointPath(extension.endpoint_path);
      setEndpointMethod(extension.endpoint_method);
      setEnabled(extension.enabled);
    } else {
      setName("");
      setDisplayName("");
      setType("cms");
      setEndpointPath("/");
      setEndpointMethod("GET");
      setEnabled(true);
    }
    setError(null);
  }, [extension, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();
    const trimmedEndpointPath = endpointPath.trim();

    if (!trimmedName) {
      setError("Name is required");
      return;
    }

    if (/\s/.test(trimmedName)) {
      setError("Name cannot contain spaces");
      return;
    }

    if (!trimmedEndpointPath) {
      setError("Endpoint path is required");
      return;
    }

    if (!trimmedEndpointPath.startsWith("/")) {
      setError("Endpoint path must start with '/'");
      return;
    }

    if (trimmedEndpointPath.includes("://")) {
      setError("Endpoint path must be a path, not a URL");
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({
        name: trimmedName,
        display_name: displayName.trim(),
        type,
        endpoint_path: trimmedEndpointPath,
        endpoint_method: endpointMethod,
        enabled,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save extension");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{extension ? "Edit Extension" : "Add Extension"}</DialogTitle>
          <DialogDescription>
            {extension
              ? "Update extension details below."
              : "Create a new extension for this tenant."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-destructive/10 text-destructive p-3 rounded-md text-sm">{error}</div>}

          <div className="grid gap-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="sample-extension"
              disabled={isSubmitting}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="display-name">Display Name</Label>
            <Input
              id="display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Optional title in sidebar"
              disabled={isSubmitting}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="type">Type</Label>
            <Select value={type} onValueChange={(value) => setType(value as ExtensionType)} disabled={isSubmitting}>
              <SelectTrigger id="type" className="w-full">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {EXTENSION_TYPES.map((itemType) => (
                  <SelectItem key={itemType} value={itemType}>
                    {itemType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="endpoint-path">Endpoint Path</Label>
              <Input
                id="endpoint-path"
                value={endpointPath}
                onChange={(e) => setEndpointPath(e.target.value)}
                placeholder="/sample-extension"
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="endpoint-method">HTTP Method</Label>
              <Select value={endpointMethod} onValueChange={setEndpointMethod} disabled={isSubmitting}>
                <SelectTrigger id="endpoint-method" className="w-full">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  {HTTP_METHODS.map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="enabled">Enabled</Label>
            <Select
              value={enabled ? "true" : "false"}
              onValueChange={(value) => setEnabled(value === "true")}
              disabled={isSubmitting}
            >
              <SelectTrigger id="enabled" className="w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Enabled</SelectItem>
                <SelectItem value="false">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : extension ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
